import { prefersReducedMotion } from '../lenis'

export function initFloorplanZoom() {
  document.querySelectorAll('[data-floorplan-zoom]').forEach((viewport) => {
    const image = viewport.querySelector('[data-floorplan-zoom-image]')
    const drawing = viewport.closest('.floorplan-drawing')
    const zoomIn = drawing?.querySelector('[data-floorplan-zoom-in]')
    const zoomOut = drawing?.querySelector('[data-floorplan-zoom-out]')
    const zoomStatus = drawing?.querySelector('[data-floorplan-zoom-status]')
    const minZoom = 1
    const maxZoom = 1.75
    const zoomStep = 0.25
    let currentZoom = minZoom
    let targetZoom = minZoom
    let zoomFrame = 0
    let zoomStart = minZoom
    let zoomStartedAt = 0
    let dragFrame = 0
    let momentumFrame = 0
    let isDragging = false
    let dragX = 0
    let dragY = 0
    let pointerX = 0
    let pointerY = 0
    let panX = 0
    let panY = 0
    let velocityX = 0
    let velocityY = 0
    let previousTime = 0

    if (!image || !zoomIn || !zoomOut) return

    const getPanBounds = (zoom = currentZoom) => {
      const viewportStyles = getComputedStyle(viewport)
      const availableWidth =
        viewport.clientWidth -
        Number.parseFloat(viewportStyles.paddingLeft) -
        Number.parseFloat(viewportStyles.paddingRight)
      const availableHeight =
        viewport.clientHeight -
        Number.parseFloat(viewportStyles.paddingTop) -
        Number.parseFloat(viewportStyles.paddingBottom)

      return {
        x: Math.max((image.offsetWidth * zoom - availableWidth) / 2, 0),
        y: Math.max((image.offsetHeight * zoom - availableHeight) / 2, 0),
      }
    }

    const renderPan = (nextX, nextY, zoom = currentZoom) => {
      const bounds = getPanBounds(zoom)

      panX = Math.min(Math.max(nextX, -bounds.x), bounds.x)
      panY = Math.min(Math.max(nextY, -bounds.y), bounds.y)
      image.style.setProperty('--floorplan-pan-x', `${panX}px`)
      image.style.setProperty('--floorplan-pan-y', `${panY}px`)
    }

    const stopMomentum = () => {
      cancelAnimationFrame(momentumFrame)
      momentumFrame = 0
    }

    const renderDrag = () => {
      if (!isDragging) {
        dragFrame = 0
        return
      }

      const now = performance.now()
      const elapsed = Math.max(now - previousTime, 1)
      const deltaX = pointerX - dragX
      const deltaY = pointerY - dragY

      renderPan(panX + deltaX, panY + deltaY)
      velocityX = velocityX * 0.55 + (deltaX / elapsed) * 16 * 0.45
      velocityY = velocityY * 0.55 + (deltaY / elapsed) * 16 * 0.45
      dragX = pointerX
      dragY = pointerY
      previousTime = now
      dragFrame = requestAnimationFrame(renderDrag)
    }

    const renderZoom = (nextZoom) => {
      const previousZoom = currentZoom

      currentZoom = nextZoom
      image.style.setProperty('--floorplan-zoom', Math.round(currentZoom * 1000) / 1000)
      viewport.dataset.zoomed = String(currentZoom > minZoom + 0.01)

      if (previousZoom > 0) {
        const ratio = currentZoom / previousZoom
        renderPan(panX * ratio, panY * ratio)
      }
    }

    const animateZoom = (time) => {
      const duration = 420
      const progress = Math.min((time - zoomStartedAt) / duration, 1)
      const eased = 1 - (1 - progress) ** 4

      renderZoom(zoomStart + (targetZoom - zoomStart) * eased)

      if (progress >= 1) {
        renderZoom(targetZoom)
        image.style.willChange = 'auto'
        zoomFrame = 0
        return
      }

      zoomFrame = requestAnimationFrame(animateZoom)
    }

    const updateZoom = (nextZoom) => {
      stopMomentum()
      targetZoom = Math.min(Math.max(nextZoom, minZoom), maxZoom)
      zoomIn.disabled = targetZoom >= maxZoom
      zoomOut.disabled = targetZoom <= minZoom
      if (zoomStatus) zoomStatus.textContent = `Zoom: ${Math.round(targetZoom * 100)}%`

      cancelAnimationFrame(zoomFrame)

      if (prefersReducedMotion.matches) {
        renderZoom(targetZoom)
        zoomFrame = 0
        return
      }

      image.style.willChange = 'transform'
      zoomStart = currentZoom
      zoomStartedAt = performance.now()
      zoomFrame = requestAnimationFrame(animateZoom)
    }

    const applyMomentum = () => {
      velocityX *= 0.92
      velocityY *= 0.92

      const previousPanX = panX
      const previousPanY = panY

      renderPan(panX + velocityX, panY + velocityY)

      if (panX === previousPanX) velocityX = 0
      if (panY === previousPanY) velocityY = 0

      if (Math.abs(velocityX) < 0.1 && Math.abs(velocityY) < 0.1) {
        momentumFrame = 0
        return
      }

      momentumFrame = requestAnimationFrame(applyMomentum)
    }

    viewport.addEventListener('pointerdown', (event) => {
      if (targetZoom <= minZoom || event.button !== 0) return

      stopMomentum()
      isDragging = true
      dragX = event.clientX
      dragY = event.clientY
      pointerX = event.clientX
      pointerY = event.clientY
      velocityX = 0
      velocityY = 0
      previousTime = performance.now()
      viewport.dataset.dragging = 'true'
      viewport.setPointerCapture(event.pointerId)
      dragFrame = requestAnimationFrame(renderDrag)
    })

    viewport.addEventListener('pointermove', (event) => {
      if (!isDragging) return

      pointerX = event.clientX
      pointerY = event.clientY
    })

    const endDrag = (event) => {
      if (!isDragging) return

      isDragging = false
      cancelAnimationFrame(dragFrame)
      dragFrame = 0
      delete viewport.dataset.dragging
      if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId)

      if (!prefersReducedMotion.matches) momentumFrame = requestAnimationFrame(applyMomentum)
    }

    viewport.addEventListener('pointerup', endDrag)
    viewport.addEventListener('pointercancel', endDrag)
    viewport.addEventListener('dragstart', (event) => event.preventDefault())

    zoomIn.addEventListener('click', () => updateZoom(targetZoom + zoomStep))
    zoomOut.addEventListener('click', () => updateZoom(targetZoom - zoomStep))
    viewport.addEventListener('floorplan:reset', () => {
      cancelAnimationFrame(dragFrame)
      dragFrame = 0
      isDragging = false
      delete viewport.dataset.dragging
      stopMomentum()
      panX = 0
      panY = 0
      renderPan(0, 0, minZoom)
      updateZoom(minZoom)
    })

    window.addEventListener('resize', () => renderPan(panX, panY))
  })
}
